import { css, useTheme } from "@emotion/react";
import clsx from "clsx";
import omit from "lodash/fp/omit";
import { useContext, useMemo } from "react";

import backgroundColorContext from "./backgroundColorContext.js";
import cardComponentContext from "./cardComponentContext";
import CardMedia from "./CardMedia.jsx";
import getVisualColor from "./getVisualColor.js";
import mergeProps from "./mergeProps.js";
import parseStyle from "./parseStyle";
import useClickable from "./useClickable.jsx";
import useComponentWidth from "./useComponentWidth";
import useThemeProps from "./useThemeProps.jsx";

export default function Card(props) {
  const theme = useTheme();
  props = useThemeProps({
    props,
    name: "Card",
  });

  let {
    breakpoint = 160,
    horizontalProps = {},
    forceHorizontal = false,
  } = props;
  props = omit(["breakpoint", "horizontalProps", "forceHorizontal"], props);
  const [width, widthRef] = useComponentWidth();
  const breakpointReached = width >= theme.getLengthPx(breakpoint);
  const isHorizontal = forceHorizontal || breakpointReached;
  const hideMedia = forceHorizontal && !breakpointReached;
  props = mergeProps(props, isHorizontal ? horizontalProps : {});

  let {
    className,
    color = "auto",
    autoColor = "gray",
    hoverColor = "hover",
    textColor,
    borderRadius = 1,
    style = {},
    hoverStyle = {},
    focusStyle = {},
    link,
    title,
    children,
    mirrored = false,
    padding = 6,
    keepPadding = false,
    image,
    mediaColspan = 7,
    mediaAspectRatio = 16 / 9,
    horizontalContentMinWidth = 64,
    horizontalContentMaxWidth = 96,
    contentAlign,
    mediaBelow = false,
    ...restProps
  } = props;

  const clickable = useClickable(link || {});
  const { Element, props: linkProps } = clickable;

  let containerBackgroundColor = useContext(backgroundColorContext);

  if (color === "auto") {
    const isBackgroundWhite =
      containerBackgroundColor === theme.getColor("white");
    color = isBackgroundWhite ? autoColor : "white";
  }

  let backgroundColor = theme.getColor(color);
  let visualColor = getVisualColor(containerBackgroundColor, backgroundColor);
  let isColorSameAsContainer =
    visualColor && visualColor === containerBackgroundColor;

  const fontColor = textColor
    ? theme.getColor(textColor)
    : theme.getColor(`${visualColor}.text`);

  const iconColor = theme.getColor(`${visualColor}.icon`);
  const linkColor = theme.getColor(`${visualColor}.link`);

  let stripPadding = !keepPadding && isColorSameAsContainer;

  // const [titleElement, setTitleElement] = useState(true);
  // const hasTitleComponent = useDebounce(!!titleElement, 50);

  const MaybeClickable = useMemo(
    () =>
      title
        ? ({ children, className }) => (
            <div className={className}>{children}</div>
          )
        : ({ clickable, ...props }) => {
            const { Element, props: linkProps, isInteractive } = clickable;
            return (
              <Element
                {...linkProps}
                {...props}
                className={clsx(
                  "wsui-card-link",
                  linkProps.className,
                  props.className,
                )}
                css={css`
                  cursor: ${isInteractive ? "pointer" : null};
                `}
              />
            );
          },
    [!!title, Element],
  );

  const componentContext = {
    clickable,
    // hasTitleComponent,
    link,
    title,
    isHorizontal,
    mirrored,
    hasImage: !!image && !hideMedia,
    padding,
    stripPadding,
    color,
    mediaColspan,
    mediaAspectRatio,
    borderRadius,
    isColorSameAsContainer,
    horizontalContentMinWidth,
    horizontalContentMaxWidth,
    contentAlign,
    mediaBelow,
  };

  hoverStyle = {
    backgroundColor: [color, hoverColor],
    ...hoverStyle,
  };
  focusStyle = {
    outline: `2px solid`,
    outlineColor: theme.getColor(["primary"]),
    ...focusStyle,
  };

  return (
    <article
      ref={widthRef}
      {...restProps}
      className={clsx(className, "wsui-card", linkProps.className)}
      css={css`
        position: relative;
        isolation: isolate;
        overflow: hidden;
        width: auto;
        padding: ${stripPadding
          ? theme.getLength(Math.max(2, borderRadius))
          : 0};
        margin: ${stripPadding
          ? theme.getLength(-Math.max(2, borderRadius))
          : 0};
        background: ${backgroundColor};
        color: ${fontColor};
        --icon-color: ${iconColor};
        --link-color: ${linkColor};
        border-radius: ${theme.getLength(borderRadius)};
        ${parseStyle(style, theme)};
        page-break-inside: avoid;
        /* overflow: hidden; */
        @supports not selector(:has(.wsui-card-link.wsui-is-interactive:hover)) {
          &:hover {
            ${parseStyle(hoverStyle, theme)};
          }
        }
        &:has(.wsui-card-link.wsui-is-interactive:hover) {
          ${parseStyle(hoverStyle, theme)};
        }
        @supports not
          selector(:has(.wsui-card-link.wsui-is-interactive:focus-visible)) {
          &:focus-within {
            ${parseStyle(focusStyle, theme)};
            ${parseStyle(hoverStyle, theme)};
          }
        }
        &:has(.wsui-card-link.wsui-is-interactive:focus-visible) {
          ${parseStyle(focusStyle, theme)};
          ${parseStyle(hoverStyle, theme)};
        }
      `}
    >
      <cardComponentContext.Provider value={componentContext}>
        <MaybeClickable
          clickable={clickable}
          css={css`
            ${theme.styleUtils.buttonReset}
            ${isHorizontal
              ? css`
                  display: flex;
                  align-items: stretch;
                  align-self: stretch;
                `
              : css`
                  display: flex;
                  flex-direction: column;
                `}
            color: inherit;
            text-decoration: inherit;
          `}
        >
          {typeof children === "function"
            ? children(componentContext)
            : children}
          {image && !hideMedia ? <CardMedia image={image} /> : null}
        </MaybeClickable>
      </cardComponentContext.Provider>
    </article>
  );
}
